import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import UserAvatar, { TIER_TINTS } from "@/components/app/UserAvatar";
import { AlertTriangle, Sparkles, ChevronRight, Award } from "lucide-react";
import { useNavigate } from "react-router-dom";

/**
 * Public team-conduct board — shows every team member with an active
 * warning or appreciation. Anyone in the team can see this so:
 *   • Appreciations are celebrated publicly.
 *   • Warnings are visible and taken seriously.
 * Profiles are tinted red (hard warning), orange (soft warning), or
 * emerald (appreciated) based on backend conduct_tier.
 */
export default function ConductBoard() {
  const [rows, setRows] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    api.get("/conduct/board").then(({ data }) => {
      if (!cancelled) setRows(data || []);
    }).catch(() => { if (!cancelled) setRows([]); });
    return () => { cancelled = true; };
  }, []);

  if (!rows || rows.length === 0) return null;

  const hard = rows.filter((r) => r.conduct_tier === "hard");
  const soft = rows.filter((r) => r.conduct_tier === "soft");
  const appr = rows.filter((r) => r.conduct_tier === "appreciation");

  return (
    <section className="bg-white border border-zinc-200 rounded-md p-4 sm:p-5" data-testid="conduct-board">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <Award className="w-4 h-4 text-zinc-500" />
          <h3 className="font-display text-base sm:text-lg font-bold tracking-tight">Team recognition</h3>
        </div>
        <div className="flex items-center gap-3 text-[11px] uppercase tracking-widest">
          {hard.length > 0 && <span className="text-red-700"><strong>{hard.length}</strong> hard</span>}
          {soft.length > 0 && <span className="text-orange-700"><strong>{soft.length}</strong> soft</span>}
          {appr.length > 0 && <span className="text-emerald-700"><strong>{appr.length}</strong> appreciated</span>}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
        {rows.map((r) => {
          const tint = TIER_TINTS[r.conduct_tier] || TIER_TINTS.none;
          const latest = r.conduct_tier === "appreciation" ? r.latest_appreciation : r.latest_warning;
          return (
            <div
              key={r.user_id}
              className={`rounded-md p-3 bg-white ${tint.row}`}
              data-testid={`conduct-card-${r.user_id}`}
              data-conduct-tier={r.conduct_tier}
            >
              <div className="flex items-start gap-3">
                <UserAvatar user={r} size={40} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm truncate">{r.name}</span>
                    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest border rounded-full px-1.5 py-0.5 ${tint.pill}`}>
                      {r.conduct_tier === "appreciation" ? <Sparkles className="w-2.5 h-2.5" /> : <AlertTriangle className="w-2.5 h-2.5" />}
                      {tint.label}
                    </span>
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                    {(r.role || "").replace(/_/g, " ")}{r.department ? ` · ${r.department}` : ""}
                  </div>
                  <div className="text-[11px] text-zinc-600 mt-1.5 flex items-baseline gap-2 flex-wrap">
                    {r.conduct.hard > 0 && <span className="text-red-700"><strong>{r.conduct.hard}</strong> hard</span>}
                    {r.conduct.soft > 0 && <span className="text-orange-700"><strong>{r.conduct.soft}</strong> soft</span>}
                    {r.conduct.appreciations > 0 && <span className="text-emerald-700"><strong>{r.conduct.appreciations}</strong> appreciation{r.conduct.appreciations === 1 ? "" : "s"}</span>}
                  </div>
                  {latest?.reason && (
                    <div className="text-[12px] text-zinc-700 mt-2 italic">
                      "{latest.reason}"
                      {latest.issued_by_name && <span className="text-zinc-400 not-italic"> — {latest.issued_by_name}</span>}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={() => navigate("/conduct")}
        className="mt-3 text-[11px] font-semibold uppercase tracking-widest text-zinc-500 hover:text-zinc-900 inline-flex items-center"
        data-testid="conduct-board-full"
      >
        See full board <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
      </button>
    </section>
  );
}
